import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Send, Paperclip, Image, X, Users } from 'lucide-react';
import { Button } from './ui/button';

const Inbox = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [ws, setWs] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    if (user) {
      fetchConversations();
      connectWebSocket();
    }
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const connectWebSocket = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) {
      console.error('REACT_APP_BACKEND_URL environment variable is not set');
      return;
    }
    const wsUrl = backendUrl.replace('http://', 'ws://').replace('https://', 'wss://') + `/ws/${user.id}`;
    const websocket = new WebSocket(wsUrl);

    websocket.onopen = () => {
      console.log('WebSocket connected');
      setWs(websocket);
    };

    websocket.onmessage = (event) => {
      const message = JSON.parse(event.data);
      handleWebSocketMessage(message);
    };

    websocket.onclose = () => {
      console.log('WebSocket disconnected');
      // Attempt to reconnect after 3 seconds
      setTimeout(connectWebSocket, 3000);
    };

    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  };

  const handleWebSocketMessage = (message) => {
    switch (message.type) {
      case 'new_message':
        if (selectedConversation && message.data.conversationId === selectedConversation.id) {
          setMessages(prev => [...prev, message.data]);
        }
        fetchConversations(); // Update conversation list
        break;
      
      case 'typing':
        if (selectedConversation && message.data.conversationId === selectedConversation.id) {
          setTypingUsers(prev => ({
            ...prev,
            [message.data.userId]: message.data.isTyping
          }));
        }
        break;
      
      case 'read_receipt':
        if (selectedConversation && message.data.conversationId === selectedConversation.id) {
          setMessages(prev => prev.map(msg => 
            msg.recipientId === message.data.userId ? { ...msg, isRead: true } : msg
          ));
        }
        break;
      
      case 'pong':
        // Handle keep-alive response
        break;
    }
  };

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversations/${conversationId}/messages`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMessages(data);
        
        // Mark messages as read
        if (ws) {
          ws.send(JSON.stringify({
            type: 'mark_read',
            conversationId: conversationId
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;
    if (!selectedConversation) return;

    try {
      // Get recipient ID from conversation participants
      const recipient = selectedConversation.participants.find(p => p.id !== user.id);
      
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipientId: recipient.id,
          content: newMessage,
          attachments: attachments
        }),
      });

      if (response.ok) {
        const message = await response.json();
        setMessages(prev => [...prev, message]);
        setNewMessage('');
        setAttachments([]);
        fetchConversations(); // Update conversation list
      } else if (response.status === 403) {
        const errorData = await response.json();
        alert(errorData.detail || 'Messaging not allowed');
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    }
  };

  const uploadAttachment = async (file) => {
    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File size too large. Maximum 10MB allowed.');
      return;
    }

    // Validate file type
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf', 'text/plain', 
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      alert('File type not allowed. Supported: Images, PDF, Text, Word documents');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/upload-attachment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const attachment = await response.json();
        setAttachments(prev => [...prev, attachment]);
      } else if (response.status === 413) {
        alert('File too large. Please choose a smaller file.');
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to upload attachment');
      }
    } catch (error) {
      console.error('Error uploading attachment:', error);
      alert('Failed to upload attachment. Please try again.');
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    files.forEach(uploadAttachment);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleTyping = () => {
    if (ws && selectedConversation) {
      ws.send(JSON.stringify({
        type: 'typing',
        conversationId: selectedConversation.id,
        isTyping: true
      }));

      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set timeout to stop typing indicator
      typingTimeoutRef.current = setTimeout(() => {
        ws.send(JSON.stringify({
          type: 'typing',
          conversationId: selectedConversation.id,
          isTyping: false
        }));
      }, 2000);
    }
  };

  const searchUsers = async (email) => {
    if (!email.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/search?email=${encodeURIComponent(email)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.filter(u => u.id !== user.id)); // Exclude self
      } else if (response.status === 403) {
        // Handle messaging restriction
        const errorData = await response.json();
        console.warn('Search restricted:', errorData.detail);
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const startNewConversation = async (selectedUser) => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipientId: selectedUser.id,
          content: 'Hello!',
          attachments: []
        }),
      });

      if (response.ok) {
        setShowNewMessageModal(false);
        setNewRecipientEmail('');
        setSearchResults([]);
        fetchConversations();
      } else if (response.status === 403) {
        const errorData = await response.json();
        alert(errorData.detail || 'Messaging not allowed');
        setShowNewMessageModal(false);
        setNewRecipientEmail('');
        setSearchResults([]);
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to start conversation');
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      alert('Failed to start conversation');
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900">
      {/* Navigation */}
      <nav className="bg-black/20 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="text-3xl">⚡</div>
              <span className="text-2xl font-bold text-white">D.C. Technology Corp.</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-white/80">Welcome, {user?.name}</span>
              
              <Link to="/dashboard">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Dashboard
                </Button>
              </Link>
              
              <Link to="/profile">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Profile
                </Button>
              </Link>
              
              {/* Direct Navigation Dropdown - Consistent Style */}
              <div className="relative group">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Navigate
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Button>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-48 bg-slate-800/95 backdrop-blur-lg border border-white/20 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible  z-50">
                  <div className="py-2">
                    <Link to="/dashboard" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                      🏠 Main Dashboard
                    </Link>
                    <Link to="/jobs/manage" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                      🔧 My Jobs
                    </Link>
                    <Link to="/profile" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                      👤 Profile
                    </Link>
                    {(user?.userType === 'provider' || user?.userType === 'admin') && (
                      <Link to="/provider-dashboard" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                        📊 Provider Dashboard
                      </Link>
                    )}
                    {(user?.userType === 'admin' || user?.userType === 'supervisor') && (
                      <Link to={user?.userType === 'admin' ? '/admin' : '/supervisor-dashboard'} className="block px-4 py-2 text-white bg-white/5 transition-colors">
                        {user?.userType === 'admin' ? '⚙️ Admin Panel' : '👥 Supervisor Panel'}
                      </Link>
                    )}
                    {user?.userType === 'admin' && (
                      <Link to="/supervisor-dashboard" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                        👥 Supervisor View
                      </Link>
                    )}
                    <div className="border-t border-white/20 my-2"></div>
                    <button 
                      onClick={() => {
                        localStorage.removeItem('dcTech_user');
                        localStorage.removeItem('dcTech_token');
                        window.location.href = '/auth';
                      }}
                      className="block w-full text-left px-4 py-2 text-red-400 bg-red-500/20 transition-colors"
                    >
                      🚪 Logout
                    </button>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={() => {
                  localStorage.removeItem('dcTech_user');
                  localStorage.removeItem('dcTech_token');
                  window.location.href = '/auth';
                }}
                variant="outline" 
                className="border-red-400/50 text-red-400 bg-red-500/20"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-lg shadow-lg overflow-hidden" style={{ height: '80vh' }}>
          <div className="flex h-full">
            {/* Conversations Sidebar */}
            <div className="w-1/3 border-r border-gray-200 flex flex-col">
              {/* Header */}
              <div className="p-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    Messages
                  </h2>
                  <button
                    onClick={() => setShowNewMessageModal(true)}
                    className="bg-white/20 bg-white/15 rounded-full p-2 transition-colors"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Conversations List */}
              <div className="flex-1 overflow-y-auto">
                {conversations.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No conversations yet</p>
                    <button
                      onClick={() => setShowNewMessageModal(true)}
                      className="mt-2 text-blue-500 hover:text-blue-700"
                    >
                      Start a conversation
                    </button>
                  </div>
                ) : (
                  conversations.map((conversation) => {
                    const otherParticipant = conversation.participants.find(p => p.id !== user.id);
                    const unreadCount = conversation.unreadCount?.[user.id] || 0;
                    
                    return (
                      <div
                        key={conversation._id}
                        onClick={() => {
                          setSelectedConversation(conversation);
                          fetchMessages(conversation._id);
                        }}
                        className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                          selectedConversation?._id === conversation._id ? 'bg-blue-50 border-blue-200' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                                {otherParticipant?.name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div className="flex-1">
                                <h3 className="font-semibold text-gray-900">{otherParticipant?.name}</h3>
                                <p className="text-sm text-gray-500 capitalize">{otherParticipant?.type}</p>
                              </div>
                            </div>
                            {conversation.lastMessage && (
                              <p className="text-sm text-gray-600 mt-1 truncate">
                                {conversation.lastMessage}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            {conversation.lastMessageAt && (
                              <p className="text-xs text-gray-400">
                                {formatTime(conversation.lastMessageAt)}
                              </p>
                            )}
                            {unreadCount > 0 && (
                              <span className="inline-block bg-red-500 text-white text-xs rounded-full px-2 py-1 mt-1">
                                {unreadCount}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 bg-white border-b border-gray-200">
                    {(() => {
                      const otherParticipant = selectedConversation.participants.find(p => p.id !== user.id);
                      return (
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                            {otherParticipant?.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{otherParticipant?.name}</h3>
                            <p className="text-sm text-gray-500 capitalize">{otherParticipant?.type}</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((message) => {
                      const isOwnMessage = message.senderId === user.id;
                      
                      return (
                        <div
                          key={message._id}
                          className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                              isOwnMessage
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 text-gray-900'
                            }`}
                          >
                            {!isOwnMessage && (
                              <p className="text-xs opacity-75 mb-1">{message.senderName}</p>
                            )}
                            <p>{message.content}</p>
                            
                            {/* Attachments */}
                            {message.attachments && message.attachments.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {message.attachments.map((attachment, index) => (
                                  <div key={index} className="flex items-center space-x-2">
                                    {attachment.fileType.startsWith('image/') ? (
                                      <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}${attachment.fileUrl}`}
                                        alt={attachment.fileName}
                                        className="max-w-xs rounded"
                                      />
                                    ) : (
                                      <a
                                        href={`${process.env.REACT_APP_BACKEND_URL}${attachment.fileUrl}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center space-x-2 text-sm underline"
                                      >
                                        <Paperclip className="w-4 h-4" />
                                        <span>{attachment.fileName}</span>
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            <div className="flex items-center justify-between mt-1">
                              <p className="text-xs opacity-75">
                                {formatTime(message.createdAt)}
                              </p>
                              {isOwnMessage && (
                                <span className="text-xs opacity-75">
                                  {message.isRead ? '✓✓' : '✓'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Typing Indicators */}
                    {Object.entries(typingUsers).map(([userId, isTyping]) => 
                      isTyping && userId !== user.id ? (
                        <div key={userId} className="flex justify-start">
                          <div className="bg-gray-200 text-gray-900 px-4 py-2 rounded-lg">
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                              <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                              <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                            </div>
                          </div>
                        </div>
                      ) : null
                    )}
                    
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <div className="p-4 bg-white border-t border-gray-200">
                    {/* Attachments Preview */}
                    {attachments.length > 0 && (
                      <div className="mb-3 flex flex-wrap gap-2">
                        {attachments.map((attachment, index) => (
                          <div key={index} className="relative bg-gray-100 rounded-lg p-2 flex items-center space-x-2">
                            {attachment.fileType && attachment.fileType.startsWith('image/') ? (
                              <Image className="w-4 h-4" />
                            ) : (
                              <Paperclip className="w-4 h-4" />
                            )}
                            <span className="text-sm truncate max-w-32">{attachment.fileName || 'Attachment'}</span>
                            <button
                              onClick={() => removeAttachment(index)}
                              className="text-red-500 hover:text-red-700"
                              aria-label="Remove attachment"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept="image/*,.pdf,.doc,.docx,.txt"
                        multiple
                        className="hidden"
                        aria-label="Upload file"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="text-gray-500  p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        aria-label="Attach file"
                        title="Attach file"
                      >
                        <Paperclip className="w-5 h-5" />
                      </button>
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => {
                          setNewMessage(e.target.value);
                          handleTyping();
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                          }
                        }}
                        placeholder={selectedConversation ? "Type a message..." : "Select a conversation to start messaging"}
                        disabled={!selectedConversation}
                        className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        aria-label="Message input"
                      />
                      <button
                        onClick={sendMessage}
                        disabled={!selectedConversation || (!newMessage.trim() && attachments.length === 0)}
                        className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg px-4 py-2 transition-colors"
                        aria-label="Send message"
                        title="Send message"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">Select a conversation to start messaging</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* New Message Modal */}
      {showNewMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Start New Conversation</h3>
            <input
              type="email"
              value={newRecipientEmail}
              onChange={(e) => {
                setNewRecipientEmail(e.target.value);
                searchUsers(e.target.value);
              }}
              placeholder="Enter email address..."
              className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg mb-4">
                {searchResults.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => startNewConversation(user)}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email} • {user.userType}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowNewMessageModal(false);
                  setNewRecipientEmail('');
                  setSearchResults([]);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inbox;